import React, { useRef } from "react";
import { CustomPicker } from "react-color";

// --- Default Dimensions -------------------------------------------------------
// These can be overridden by passing pickerWidth and pickerHeight as props.
const DEFAULT_PICKER_WIDTH = 282; // total width (px)
const DEFAULT_PICKER_HEIGHT = 290; // total height (px)
const SLIDER_W = 20; // hue & alpha sliders: width (px)
const SLIDER_MARGIN = 10; // margin between components (px)
const CIRCLE_R = 6; // radius shared by all circular drag handles (px)

const CirclePointer = () => (
  <div
    style={{
      width: CIRCLE_R * 2,
      height: CIRCLE_R * 2,
      borderRadius: "50%",
      border: "2px solid rgba(255,255,255,0.9)",
      boxShadow: "0 1px 4px rgba(0,0,0,0.4)",
      boxSizing: "border-box",
      pointerEvents: "none",
    }}
  />
);

const SaturationSquare = ({ hsl, hsv, onChange, size }) => {
  const containerRef = useRef(null);

  const updateFromClient = (clientX, clientY) => {
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
    const y = Math.max(0, Math.min(rect.height, clientY - rect.top));
    onChange({
      h: hsl.h,
      s: x / rect.width,
      v: 1 - y / rect.height,
      a: hsl.a,
      source: "hsv",
    });
  };

  const handlePointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    updateFromClient(e.clientX, e.clientY);
  };

  const handlePointerMove = (e) => {
    if (!e.buttons) return;
    updateFromClient(e.clientX, e.clientY);
  };

  const clampedLeft = Math.max(
    CIRCLE_R,
    Math.min(size - CIRCLE_R, hsv.s * size),
  );
  const clampedTop = Math.max(
    CIRCLE_R,
    Math.min(size - CIRCLE_R, (1 - hsv.v) * size),
  );

  return (
    <div
      ref={containerRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        background: `hsl(${hsl.h}, 100%, 50%)`,
        cursor: "crosshair",
        touchAction: "none",
        userSelect: "none",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "linear-gradient(to right, #fff, transparent)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "linear-gradient(to bottom, transparent, #000)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          left: clampedLeft,
          top: clampedTop,
          transform: `translate(-50%, -50%)`,
          zIndex: 1,
        }}
      >
        <CirclePointer />
      </div>
    </div>
  );
};

const VerticalHueSlider = ({ hsl, onChange, sliderHeight }) => {
  const containerRef = useRef(null);

  const updateFromClientY = (clientY) => {
    const rect = containerRef.current.getBoundingClientRect();
    const top = Math.max(0, Math.min(rect.height, clientY - rect.top));
    onChange({
      h: (1 - top / rect.height) * 360,
      s: hsl.s,
      l: hsl.l,
      a: hsl.a,
      source: "hsl",
    });
  };

  const handlePointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    updateFromClientY(e.clientY);
  };

  const handlePointerMove = (e) => {
    if (!e.buttons) return;
    updateFromClientY(e.clientY);
  };

  const clampedTop = Math.max(
    CIRCLE_R,
    Math.min(sliderHeight - CIRCLE_R, (1 - hsl.h / 360) * sliderHeight),
  );

  return (
    <div
      ref={containerRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        background:
          "linear-gradient(to top, #f00 0%, #ff0 17%, #0f0 33%, #0ff 50%, #00f 67%, #f0f 83%, #f00 100%)",
        cursor: "crosshair",
        touchAction: "none",
        userSelect: "none",
      }}
    >
      <div
        style={{
          position: "absolute",
          left: "50%",
          top: clampedTop,
          transform: `translate(-50%, -${CIRCLE_R}px)`,
        }}
      >
        <CirclePointer />
      </div>
    </div>
  );
};

const VerticalAlphaSlider = ({ rgb, onChange, sliderHeight }) => {
  const containerRef = useRef(null);

  const updateFromClientY = (clientY) => {
    const rect = containerRef.current.getBoundingClientRect();
    const top = Math.max(0, Math.min(rect.height, clientY - rect.top));
    onChange({ ...rgb, a: top / rect.height, source: "rgb" });
  };

  const handlePointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    updateFromClientY(e.clientY);
  };

  const handlePointerMove = (e) => {
    if (!e.buttons) return;
    updateFromClientY(e.clientY);
  };

  const clampedTop = Math.max(
    CIRCLE_R,
    Math.min(sliderHeight - CIRCLE_R, rgb.a * sliderHeight),
  );

  return (
    <div
      ref={containerRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        cursor: "crosshair",
        touchAction: "none",
        userSelect: "none",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "conic-gradient(#ccc 25%, #fff 25%, #fff 75%, #ccc 75%)",
          backgroundSize: "8px 8px",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: `linear-gradient(to bottom, rgba(${rgb.r},${rgb.g},${rgb.b},0) 0%, rgba(${rgb.r},${rgb.g},${rgb.b},1) 100%)`,
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          left: "50%",
          top: clampedTop,
          transform: `translate(-50%, -${CIRCLE_R}px)`,
          zIndex: 1,
        }}
      >
        <CirclePointer />
      </div>
    </div>
  );
};

class MyColorPicker extends React.Component {
  render() {
    const pickerWidth = this.props.pickerWidth ?? DEFAULT_PICKER_WIDTH;
    const pickerHeight = this.props.pickerHeight ?? DEFAULT_PICKER_HEIGHT;

    // Calculate saturation square size (it's square, so use height for both dimensions)
    const satSize = pickerHeight;

    // Calculate slider height to match saturation square
    const sliderHeight = pickerHeight;

    return (
      <div style={{ display: "flex", alignItems: "flex-start" }}>
        <div
          style={{
            position: "relative",
            width: `${satSize}px`,
            height: `${satSize}px`,
            minWidth: `${satSize}px`,
            minHeight: `${satSize}px`,
            borderRadius: "12px",
            overflow: "hidden",
            flex: "0 0 auto",
          }}
        >
          <SaturationSquare
            hsl={this.props.hsl}
            hsv={this.props.hsv}
            onChange={this.props.onChange}
            size={satSize}
          />
        </div>

        <div
          style={{
            position: "relative",
            width: `${SLIDER_W}px`,
            height: `${sliderHeight}px`,
            minWidth: `${SLIDER_W}px`,
            minHeight: `${sliderHeight}px`,
            marginLeft: `${SLIDER_MARGIN}px`,
            borderRadius: "8px",
            overflow: "hidden",
            flex: "0 0 auto",
          }}
        >
          <VerticalHueSlider
            hsl={this.props.hsl}
            onChange={this.props.onChange}
            sliderHeight={sliderHeight}
          />
        </div>

        <div
          style={{
            position: "relative",
            width: `${SLIDER_W}px`,
            height: `${sliderHeight}px`,
            minWidth: `${SLIDER_W}px`,
            minHeight: `${sliderHeight}px`,
            marginLeft: `${SLIDER_MARGIN}px`,
            borderRadius: "8px",
            overflow: "hidden",
            flex: "0 0 auto",
          }}
        >
          <VerticalAlphaSlider
            rgb={this.props.rgb}
            onChange={this.props.onChange}
            sliderHeight={sliderHeight}
          />
        </div>
      </div>
    );
  }
}

export default CustomPicker(MyColorPicker);
